// Role: Doctor.
// Description: List of patients at home page.
import React from 'react';
import { withTranslation } from 'react-i18next';
import { PageWrapper, Paper, FlexTableRow } from 'virtues-bootstrap';
import PropTypes from 'prop-types';
import { map } from 'ramda';
import { requestPatientList } from 'services/api/request/patient';
import { requestPatientsObservationsStatuses } from 'services/api/request/reports';
import {
  getPatientsList,
  getJoinedIdsFromPatientsList,
  getPatientsListWithWarning,
  getPatientsListWithReport,
} from 'store/selectors/patient';
import { REGISTERED } from 'constants/observationStatuses';
import { DOCTOR } from 'constants/userRoles';

import usePagination from 'hook/usePagination';
import useDependentData from 'hook/useDependentData';

import PaginationSection from 'components/ui/PaginationSection';
import PaperWithLoader from 'components/ui/PaperWithLoader';
import HeaderColumn from 'components/ui/HeaderColumn';
import PatientListRow from './components/PatientListRow';
import HeaderSearch from './components/HeaderSearch';
import { NAME, PATIENT_ID } from './constant';

import s from './styles.module.scss';

function ClinicianPatientList({ userId, t, role }) {
  const isDoctor = role === DOCTOR;
  const [{ data, initial, fetching, pagination, error, sorting }, getData] = usePagination({
    request: requestPatientList,
    params: { generalPracitioner: userId, sort: '_id', count: 12 },
    selector: getPatientsList,
  });
  const { total, page, count } = pagination;
  const { sort, desc } = sorting;

  const handlePageChange = selectedPage => {
    getData({
      pagesOffset: count * selectedPage,
      page: selectedPage,
    });
  };

  const [{ data: updatedData }] = useDependentData({
    request: requestPatientsObservationsStatuses,
    data,
    canWork: !!(data && data.length),
    params: {
      elements: 'status,subject',
      status: REGISTERED,
      count: 99999,
      patientsIds: getJoinedIdsFromPatientsList(data),
      performer: userId,
    },
    modifiers: [getPatientsListWithWarning, getPatientsListWithReport],
  });

  const searchFields = [
    { key: t('patient.name'), value: NAME },
    { key: t('patient.id'), value: PATIENT_ID },
  ];
  const headerColumns = [
    { title: t('patient.patientName'), key: 'name' },
    { title: t('patient.patientId'), key: '_id' },
    { title: t('patient.age'), key: 'birthdate' },
    { title: t('patient.deviceVendor') },
    { title: t('reports.deviceType') },
    { title: t('patient.activationDate') },
    { title: t('reports.recentReports') },
  ];

  return (
    <PageWrapper>
      <HeaderSearch searchFields={searchFields} onSubmit={getData} />
      <PaperWithLoader
        check={!!data.length}
        placeholder={t('patient.patientsNotFound')}
        error={error}
        loading={initial || fetching}
      >
        <Paper className={s.mainPaper} data-test="patientListBlock">
          <FlexTableRow heading>
            {headerColumns.map(({ title, key }) => (
              <HeaderColumn
                key={title}
                sort={sort}
                currentKey={key}
                title={title}
                desc={desc}
                handleClick={getData}
              />
            ))}
          </FlexTableRow>
          {map(
            ({ id, ...rest }) => (
              <PatientListRow key={id} id={id} isDoctor={isDoctor} isClinicianAbility {...rest} />
            ),
            updatedData,
          )}
        </Paper>
        <PaginationSection
          activePage={page}
          disableInitialCallback
          onPageChange={handlePageChange}
          itemsPerPage={count}
          textKey="pagination.patients"
          total={total}
        />
      </PaperWithLoader>
    </PageWrapper>
  );
}

ClinicianPatientList.propTypes = {
  userId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
};

export default withTranslation()(ClinicianPatientList);
